import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Container, Card, Fade, Typography, Chip } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { RecommendationsView } from '../components/stocks';
import { useRecommendations } from '../hooks/useStocks';
import RecommendationsSkeleton from '../components/recommendations/RecommendationsSkeleton';
import RecommendationsStatsCards from '../components/recommendations/RecommendationsStatsCards';
import HighConvictionStocksCard from '../components/recommendations/HighConvictionStocksCard';
import RecommendationsErrorState from '../components/recommendations/RecommendationsErrorState';
import RecommendationsEmptyState from '../components/recommendations/RecommendationsEmptyState';
import { useTranslation } from 'react-i18next';

const Recommendations = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeStyle, setActiveStyle] = useState('FLOW_LEADERS');

  const { data: recommendations, isLoading, error } = useRecommendations(true);
  
  // Restore scroll position
  useEffect(() => {
    const scrollKey = `scroll_${location.pathname}`;
    const savedScroll = sessionStorage.getItem(scrollKey);
    if (savedScroll) {
      setTimeout(() => {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
          mainContent.scrollTop = Number.parseInt(savedScroll, 10);
        } else {
          window.scrollTo(0, Number.parseInt(savedScroll, 10));
        }
        sessionStorage.removeItem(scrollKey);
      }, 100);
    }
  }, [location.pathname]);

  // Stats
  const stats = useMemo(() => {
    if (!recommendations) return null;
    const allStyles = Object.keys(recommendations);
    const totalStocks = allStyles.reduce((sum, style) => {
      const styleData = recommendations[style];
      const results = styleData?.results || styleData || [];
      return sum + (Array.isArray(results) ? results.length : 0);
    }, 0);
    let lastUpdated = null;
    for (const style of allStyles) {
      const styleData = recommendations[style];
      if (styleData?.asOf) { lastUpdated = styleData.asOf; break; }
      const results = styleData?.results || styleData || [];
      if (Array.isArray(results) && results.length > 0 && results[0]?.updated_at) {
        lastUpdated = results[0].updated_at; break;
      }
    }
    return { totalStyles: allStyles.length, totalStocks, lastUpdated };
  }, [recommendations]);

  // High conviction stocks - collect full data
  const highConvictionStocks = useMemo(() => {
    if (!recommendations) return [];
    const map = {};
    Object.entries(recommendations).forEach(([style, styleData]) => {
      const results = styleData?.results || [];
      results.forEach(stock => {
        if (!map[stock.ticker]) {
          map[stock.ticker] = {
            count: 0, styles: [], scores: [],
            ticker: stock.ticker,
            companyName: stock.companyName || stock.company_name,
            sector: stock.sector,
            marketCap: stock.marketCap || stock.mcap || stock.market_cap,
            marketStageOverlays: [],
            scoreLevels: [],
          };
        }
        map[stock.ticker].count += 1;
        map[stock.ticker].styles.push(style);
        if (stock.score != null) map[stock.ticker].scores.push(stock.score);
        if (stock.score_level) map[stock.ticker].scoreLevels.push(stock.score_level);
        if (stock.marketStageOverlay) {
          map[stock.ticker].marketStageOverlays.push(stock.marketStageOverlay);
        }
      });
    });
    const mostFrequent = (arr) => {
      if (!arr || arr.length === 0) return 'unknown';
      const freq = {};
      arr.forEach(k => { freq[k] = (freq[k] || 0) + 1; });
      return Object.entries(freq).reduce((a, b) => b[1] > a[1] ? b : a, ['', 0])[0];
    };
    return Object.values(map)
      .map(s => ({ ...s,
        avgScore: s.scores.length > 0 ? s.scores.reduce((a, b) => a + b, 0) / s.scores.length : null,
        avgScoreLevel: mostFrequent(s.scoreLevels),
      }))
      .filter(s => s.count >= 2)
      .sort((a, b) => b.count - a.count || (b.avgScore || 0) - (a.avgScore || 0))
      .slice(0, 15);
  }, [recommendations]);

  const highConvictionContext = useMemo(() => {
    if (!recommendations) return null;

    const marketStages = Object.values(recommendations)
      .map((styleData) => styleData?.marketStage)
      .filter(Boolean);

    if (!marketStages.length) return null;

    const firstSummary = marketStages.find((item) => item?.summary)?.summary || null;
    const uniqueStages = [...new Set(marketStages.map((item) => item?.stage).filter(Boolean))];
    const uniquePlaybooks = [...new Set(marketStages.map((item) => item?.playbook).filter(Boolean))];

    return {
      summary: firstSummary,
      stages: uniqueStages,
      playbooks: uniquePlaybooks,
      totalStylesWithStage: marketStages.length,
    };
  }, [recommendations]);

  if (isLoading) return <RecommendationsSkeleton />;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* ═══════ HEADER ═══════ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)',
          }}>
            <SmartToyIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                {t('vnstock.stocks.viewRecommendations', 'AI Khuyến Nghị Danh Mục')}
              </Typography>
              {stats?.lastUpdated && (
                <Chip 
                  label={new Date(stats.lastUpdated).toLocaleDateString('vi-VN')}
                  size="small"
                  icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', ml: 0.5 }} />}
                  sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600, bgcolor: '#f0fdf4', color: '#16a34a' }}
                />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('vnstock.recommendations.subtitle', 'Phân tích chuyên sâu, đa chiều, cập nhật liên tục từ AI')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ═══════ ERROR ═══════ */}
      <RecommendationsErrorState error={error} />

      {/* ═══════ EMPTY ═══════ */}
      {!isLoading && !error && stats?.totalStocks === 0 && <RecommendationsEmptyState />}

      {/* ═══════ STATS + CONTENT ═══════ */}
      {!error && stats?.totalStocks > 0 && (
        <>
          {/* KPI Strip */}
          <RecommendationsStatsCards
            recommendations={recommendations}
            stats={stats}
            isLoading={isLoading}
            highConvictionStocks={highConvictionStocks}
          />

          {/* High Conviction */}
          <HighConvictionStocksCard
            highConvictionStocks={highConvictionStocks}
            marketStageContext={highConvictionContext}
          />

          {/* Main Table */}
          <Fade in timeout={500}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden' }}>
              <RecommendationsView
                recommendations={recommendations}
                activeStyle={activeStyle}
                setActiveStyle={setActiveStyle}
                currentState={{ activeStyle }}
                loading={isLoading}
              />
            </Card>
          </Fade>
        </>
      )}
    </Container>
  );
};

export default Recommendations;
